import { useState, useRef, useEffect, useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import { useAuthStore } from '@/store/auth-store'
import { chatService } from '@/services/chat-service'

interface Message {
  id: string
  role: 'user' | 'assistant'
  content: string
  error?: boolean
}

/* ── Lightweight markdown renderer for chat ── */
function renderChatMarkdown(text: string) {
  // Split into lines and group into blocks (paragraphs, lists)
  const lines = text.split('\n')
  const blocks: { type: 'p' | 'ul' | 'ol'; lines: string[] }[] = []
  let current: { type: 'p' | 'ul' | 'ol'; lines: string[] } | null = null

  for (const raw of lines) {
    const line = raw.trimEnd()
    const ulMatch = line.match(/^[-*•]\s+(.+)/)
    const olMatch = line.match(/^\d+[.)]\s+(.+)/)

    if (ulMatch) {
      if (current?.type !== 'ul') {
        current = { type: 'ul', lines: [] }
        blocks.push(current)
      }
      current.lines.push(ulMatch[1])
    } else if (olMatch) {
      if (current?.type !== 'ol') {
        current = { type: 'ol', lines: [] }
        blocks.push(current)
      }
      current.lines.push(olMatch[1])
    } else if (line.trim() === '') {
      current = null
    } else {
      if (current?.type !== 'p') {
        current = { type: 'p', lines: [] }
        blocks.push(current)
      }
      current.lines.push(line)
    }
  }

  // Inline formatting: **bold**
  const inlineFmt = (s: string) => {
    const parts: (string | JSX.Element)[] = []
    let remaining = s
    let key = 0
    const boldRe = /\*\*(.+?)\*\*/
    let match = boldRe.exec(remaining)
    while (match) {
      if (match.index > 0) parts.push(remaining.slice(0, match.index))
      parts.push(<strong key={key++}>{match[1]}</strong>)
      remaining = remaining.slice(match.index + match[0].length)
      match = boldRe.exec(remaining)
    }
    if (remaining) parts.push(remaining)
    return parts.length === 1 && typeof parts[0] === 'string' ? parts[0] : <>{parts}</>
  }

  return blocks.map((block, i) => {
    if (block.type === 'ul') {
      return (
        <ul key={i} className="hc-md-ul">
          {block.lines.map((l, j) => <li key={j}>{inlineFmt(l)}</li>)}
        </ul>
      )
    }
    if (block.type === 'ol') {
      return (
        <ol key={i} className="hc-md-ol">
          {block.lines.map((l, j) => <li key={j}>{inlineFmt(l)}</li>)}
        </ol>
      )
    }
    return <p key={i} className="hc-md-p">{inlineFmt(block.lines.join(' '))}</p>
  })
}

/* ── Inline SVG icons ── */
const ChatIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
)

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
)

const SendIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="22" y1="2" x2="11" y2="13" />
    <polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
)

const HelpIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
)

/* ── Page name helper ── */
function getPageLabel(pathname: string): string {
  if (pathname.startsWith('/estimates/') && pathname.includes('/builder')) return 'Estimate Builder'
  if (pathname === '/estimates') return 'Estimates List'
  if (pathname.startsWith('/proposals')) return 'Proposals'
  if (pathname.startsWith('/reports/')) return 'Report Detail'
  if (pathname === '/reports') return 'Reports Hub'
  if (pathname.startsWith('/pricing')) return 'Pricing'
  if (pathname.startsWith('/settings')) return 'Settings'
  if (pathname === '/dashboard') return 'Dashboard'
  return pathname
}

export function HelpChatWidget() {
  const [isOpen, setIsOpen] = useState(false)
  const [messages, setMessages] = useState<Message[]>([])
  const [input, setInput] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const location = useLocation()
  const user = useAuthStore((s) => s.user)

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, isLoading])

  // Focus input when panel opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100)
    }
  }, [isOpen])

  const handleSend = useCallback(async () => {
    const text = input.trim()
    if (!text || isLoading) return

    const userMsg: Message = { id: `u-${Date.now()}`, role: 'user', content: text }
    setMessages((prev) => [...prev, userMsg])
    setInput('')
    setIsLoading(true)

    // Build history from existing messages (exclude errors)
    const history = messages
      .filter((m) => !m.error)
      .map((m) => ({ role: m.role, content: m.content }))

    try {
      const result = await chatService.send(text, history, {
        userRole: user?.role || 'unknown',
        currentPage: getPageLabel(location.pathname),
        userName: user?.displayName || user?.email || 'User',
      })

      const botMsg: Message = { id: `b-${Date.now()}`, role: 'assistant', content: result.content }
      setMessages((prev) => [...prev, botMsg])
    } catch {
      const errMsg: Message = {
        id: `e-${Date.now()}`,
        role: 'assistant',
        content: 'Sorry, something went wrong. Please try again.',
        error: true,
      }
      setMessages((prev) => [...prev, errMsg])
    } finally {
      setIsLoading(false)
    }
  }, [input, isLoading, messages, user, location.pathname])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  return (
    <>
      {/* Chat panel */}
      {isOpen && (
        <div className="hc-panel">
          {/* Header */}
          <div className="hc-header">
            <div className="hc-header-title">
              <img src="/images/paver-mark.png" alt="Paver" className="hc-header-logo" />
              Ask a Question?
            </div>
            <button className="hc-header-close" onClick={() => setIsOpen(false)}>
              <CloseIcon />
            </button>
          </div>

          {/* Messages */}
          <div className="hc-messages">
            {messages.length === 0 && (
              <div className="hc-welcome">
                <div className="hc-welcome-icon">
                  <HelpIcon />
                </div>
                Hi{user?.firstName ? ` ${user.firstName}` : ''}! I'm the Paver Help Assistant. Ask me anything about how the app works — estimates, proposals, reports, and more.
              </div>
            )}

            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`hc-msg ${
                  msg.role === 'user' ? 'hc-msg-user' : msg.error ? 'hc-msg-error' : 'hc-msg-bot'
                }`}
              >
                {msg.role === 'assistant' && !msg.error
                  ? renderChatMarkdown(msg.content)
                  : msg.content}
              </div>
            ))}

            {isLoading && (
              <div className="hc-typing">
                <div className="hc-typing-dot" />
                <div className="hc-typing-dot" />
                <div className="hc-typing-dot" />
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="hc-input-row">
            <textarea
              ref={inputRef}
              className="hc-input"
              placeholder="Ask about Paver..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              rows={1}
              disabled={isLoading}
            />
            <button
              className="hc-send"
              onClick={handleSend}
              disabled={!input.trim() || isLoading}
            >
              <SendIcon />
            </button>
          </div>
        </div>
      )}

      {/* FAB */}
      <button className="hc-fab" onClick={() => setIsOpen((o) => !o)} title="Help">
        {isOpen ? <CloseIcon /> : <ChatIcon />}
      </button>
    </>
  )
}
